// Pure logic for the Orders page "Lost customers" view: who bought from us and
// then stopped. Kept out of Orders.js so it can be tested.

const DAY_MS = 86400000;

// A "subscription order" is anything the subscription routes created - they
// stamp `subscriptionPlanId` / `subscriptionEvent`. The trailing clauses only
// catch historical orders written before those fields existed: old recurring
// charges stored a Stripe PaymentIntent id ("pi_"; storefront checkouts use
// "cs_"), and old signups were a £0 marker with no session id.
export const isSubOrder = (o) =>
  !!o.subscriptionPlanId ||
  o.subscriptionEvent != null ||
  String(o.stripeSessionId || '').startsWith('pi_') ||
  (o.total === 0 && !o.stripeSessionId && o.paymentMethod !== 'cash');

// Same person across orders: email first, then phone, then name.
export const customerKey = ({ email, phone, name } = {}) => {
  const e = String(email || '').trim().toLowerCase();
  if (e) return `e:${e}`;
  const p = String(phone || '').replace(/\D/g, '').replace(/^44/, '0');
  if (p) return `p:${p}`;
  const n = String(name || '').trim().toLowerCase();
  return n ? `n:${n}` : null;
};

const dayKey = (d) => new Date(d).toISOString().slice(0, 10);

const subAddress = (sub) => (sub?.deliveryAddress?.line1 ? sub.deliveryAddress : null);

// Subscriptions that mean the customer is still with us.
const LIVE_SUB_STATUSES = ['active', 'paused', 'pending'];

/**
 * @param orders         every order (any age); subscription orders are ignored
 * @param subscriptions  SubscriptionPlan docs with customerId populated {name,email,phone}
 * @param minDaysSince   nobody counts as lost until this long after their last purchase
 * @returns lost customers, most valuable first. `kind` is:
 *   'one-off'   - bought on a single day and never came back
 *   'stopped'   - bought on several days, then went quiet for well over their usual gap
 *   'cancelled' - cancelled their subscription and hasn't bought since
 */
export function findLostCustomers(orders = [], subscriptions = [], { minDaysSince = 45, now = Date.now() } = {}) {
  const liveSubKeys = new Set();
  const cancelledByKey = new Map();
  subscriptions.forEach((sub) => {
    const key = customerKey(sub.customerId || {});
    if (!key) return;
    if (LIVE_SUB_STATUSES.includes(sub.status)) liveSubKeys.add(key);
    else if (sub.status === 'cancelled') {
      const prev = cancelledByKey.get(key);
      const at = new Date(sub.updatedAt || sub.createdAt || now).getTime();
      if (!prev || at > prev.at) cancelledByKey.set(key, { sub, at });
    }
  });

  const byCustomer = new Map();
  orders.forEach((o) => {
    if (isSubOrder(o) || !o.timestamp) return;
    const key = customerKey(o);
    if (!key) return;
    if (!byCustomer.has(key)) byCustomer.set(key, []);
    byCustomer.get(key).push(o);
  });

  const lost = [];
  const seen = new Set();

  byCustomer.forEach((list, key) => {
    seen.add(key);
    if (liveSubKeys.has(key)) return;

    list.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const first = new Date(list[0].timestamp).getTime();
    const last  = new Date(list[list.length - 1].timestamp).getTime();
    const daysSince = Math.floor((now - last) / DAY_MS);

    // Several orders on the same day are one purchase occasion, not a habit.
    const buyingDays = new Set(list.map((o) => dayKey(o.timestamp))).size;
    const avgGapDays = buyingDays > 1 ? Math.round((last - first) / DAY_MS / (buyingDays - 1)) : null;

    const cancelled = cancelledByKey.get(key);
    let kind = null;
    if (cancelled && cancelled.at >= last) {
      kind = 'cancelled';
    } else if (buyingDays === 1) {
      if (daysSince >= minDaysSince) kind = 'one-off';
    } else if (daysSince >= Math.max(minDaysSince, 2 * avgGapDays)) {
      kind = 'stopped';
    }
    if (!kind) return;

    const itemCounts = {};
    list.forEach((o) => (o.items || []).forEach((it) => {
      if (it.name === 'Local delivery') return;
      itemCounts[it.name] = (itemCounts[it.name] || 0) + (it.quantity || 1);
    }));
    const favourites = Object.entries(itemCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name]) => name);

    const latest = list[list.length - 1];
    const withContact = [...list].reverse();
    lost.push({
      key, kind,
      name:  latest.name || cancelled?.sub.customerId?.name || '',
      email: withContact.find((o) => o.email)?.email || cancelled?.sub.customerId?.email || '',
      phone: withContact.find((o) => o.phone)?.phone || cancelled?.sub.customerId?.phone || '',
      address: withContact.find((o) => o.address?.line1)?.address || subAddress(cancelled?.sub),
      orderCount: list.length,
      buyingDays,
      totalSpent: list.reduce((sum, o) => sum + (o.total || 0), 0),
      firstOrder: new Date(first),
      lastOrder: new Date(Math.max(last, cancelled?.at ?? 0)),
      daysSince: cancelled ? Math.floor((now - Math.max(last, cancelled.at)) / DAY_MS) : daysSince,
      avgGapDays,
      favourites,
      subscription: cancelled?.sub || null,
    });
  });

  // Cancelled subscribers who never placed a one-off order.
  cancelledByKey.forEach(({ sub, at }, key) => {
    if (seen.has(key) || liveSubKeys.has(key)) return;
    const c = sub.customerId || {};
    lost.push({
      key, kind: 'cancelled',
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      address: subAddress(sub),
      orderCount: 0, buyingDays: 0, totalSpent: 0,
      firstOrder: sub.startDate ? new Date(sub.startDate) : null,
      lastOrder: new Date(at),
      daysSince: Math.floor((now - at) / DAY_MS),
      avgGapDays: null,
      favourites: (sub.items || []).map((i) => i.name).slice(0, 3),
      subscription: sub,
    });
  });

  return lost.sort((a, b) => b.totalSpent - a.totalSpent || a.daysSince - b.daysSince);
}
